import { ReactNode } from 'react';
import { ConditionalValue, EmptyState, StackProps } from '@chakra-ui/react';

import { ThemeColour, VStack } from '../../chakra';
import { IconType, MdCheckCircleOutline, MdError, MdInfo, MdWarning } from '../../icons';
import { Status } from '../../types';

interface BaseResultProps {
  title: ReactNode;
  text?: ReactNode;
  actions?: ReactNode;
  size?: ConditionalValue<'sm' | 'md' | 'lg' | undefined>;
  gap?: StackProps['gap'];
}

export type ResultProps = BaseResultProps &
  // Either provide status - icon and colour set automatically
  (| { status: Status; icon?: never; colour?: never }
    // Or provide icon and colour
    | {
        icon: IconType;
        colour: ThemeColour;
        status?: never;
      }
  );

const STATUS_ICONS: Record<Status, IconType> = {
  [Status.ERROR]: MdError,
  [Status.WARNING]: MdWarning,
  [Status.INFO]: MdInfo,
  [Status.SUCCESS]: MdCheckCircleOutline,
  [Status.NEUTRAL]: MdInfo,
};

export function Result({ status, title, text, actions, icon, colour, size, gap }: ResultProps) {
  const iconToUse = status ? STATUS_ICONS[status] : icon;

  return (
    <EmptyState.Root size={size} gap={gap || 0} status={status || Status.INFO}>
      <EmptyState.Content>
        <EmptyState.Indicator as={iconToUse} color={colour} />
        <VStack gap={0} textAlign="center">
          <EmptyState.Title>{title}</EmptyState.Title>
          <EmptyState.Description>{text}</EmptyState.Description>
        </VStack>
        {actions}
      </EmptyState.Content>
    </EmptyState.Root>
  );
}
